import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { isRedirectableHash, type RequestMiss } from "shared";
import { ApiException } from "@/api/client";
import { createBlock } from "@/api/blocks";
import { getLogsByIp } from "@/api/logs";
import { UaCell } from "@/components/UaCell";
import { formatDt } from "@/lib/format";

/** Segment usable as a short-link hash? ("/wp-login.php" → no, "/zz9zz" → yes) */
function linkableSegment(path: string): string | null {
	const segment = path.startsWith("/") ? path.slice(1) : path;
	if (!segment || segment.includes("/")) return null;
	return isRedirectableHash(segment) ? segment : null;
}

export function LogIpPage() {
	const [params] = useSearchParams();
	const ip = params.get("ip")?.trim() ?? "";
	const [misses, setMisses] = useState<RequestMiss[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [blocked, setBlocked] = useState(false);

	const load = useCallback(async () => {
		if (!ip) {
			setLoading(false);
			setError("Missing IP");
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const res = await getLogsByIp(ip);
			setMisses(res.misses);
		} catch (err) {
			setError(err instanceof ApiException ? err.message : "Failed to load");
		} finally {
			setLoading(false);
		}
	}, [ip]);

	useEffect(() => {
		void load();
	}, [load]);

	async function blockIp() {
		if (!ip || blocked) return;
		if (!confirm(`Block IP "${ip}" globally (all links)?`)) return;
		try {
			await createBlock({ kind: "ip", value: ip });
			setBlocked(true);
		} catch (err) {
			setError(err instanceof ApiException ? err.message : "Block failed");
		}
	}

	if (loading) {
		return <p className="text-fg-muted text-sm">Loading…</p>;
	}
	if (!ip) {
		return (
			<div className="space-y-2">
				<p className="text-fg-muted">{error ?? "Not found"}</p>
				<Link to="/ips" className="text-accent hover:underline text-sm">
					Back
				</Link>
			</div>
		);
	}

	const totalHits = misses.reduce((sum, m) => sum + m.hitCount, 0);
	const pathCount = new Set(misses.map((m) => m.path)).size;
	const latest = misses[0];

	return (
		<div className="space-y-8">
			<div>
				<p className="text-sm text-fg-muted">
					<Link to="/ips" className="hover:underline">
						IPs
					</Link>{" "}
					/
				</p>
				<h1 className="mt-1 font-mono text-xl text-accent break-all">{ip}</h1>
				<div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
					<span className="font-mono">
						{totalHits} hits · {pathCount} path
						{pathCount === 1 ? "" : "s"}
					</span>
					{latest && (
						<>
							<span className="rounded-full bg-surface-raised px-2 py-0.5 font-medium">
								{latest.lastCountry ?? "—"}
								{latest.lastAsn != null && ` · AS${latest.lastAsn}`}
							</span>
							<span>last seen {formatDt(latest.lastSeenAt)}</span>
						</>
					)}
					<button
						type="button"
						disabled={blocked}
						className="text-fg-muted hover:text-rose-600 disabled:opacity-40 dark:disabled:text-rose-400/40 disabled:hover:text-fg-muted"
						onClick={() => void blockIp()}
					>
						{blocked ? "IP blocked" : "Block IP"}
					</button>
				</div>
			</div>

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			<section className="space-y-3">
				<h2 className="text-sm font-medium text-fg-muted">Paths this IP probed</h2>
				<div className="overflow-x-auto rounded-xl border border-border">
					<table className="w-full min-w-[880px] text-left text-sm">
						<thead className="border-b border-border bg-surface-raised text-fg-muted">
							<tr>
								<th className="px-3 py-2 font-medium">Path</th>
								<th className="px-3 py-2 font-medium">Method</th>
								<th className="px-3 py-2 font-medium">Query</th>
								<th className="px-3 py-2 font-medium">Status</th>
								<th
									className="px-3 py-2 font-medium"
									title="User agent of the most recent hit"
								>
									Browser / OS
								</th>
								<th className="px-3 py-2 font-medium">Hits</th>
								<th className="px-3 py-2 font-medium">Last seen</th>
								<th className="px-3 py-2 font-medium">Actions</th>
							</tr>
						</thead>
						<tbody>
							{misses.map((m) => {
								const segment = linkableSegment(m.path);
								return (
									<tr key={m.id} className="border-b border-border last:border-0">
										<td className="max-w-[20rem] px-3 py-2">
											<a
												href={m.path}
												target="_blank"
												rel="noreferrer"
												title="Open this path"
												className="block truncate font-mono text-xs text-accent hover:underline"
											>
												{m.path}
											</a>
										</td>
										<td className="px-3 py-2 font-mono text-xs">{m.method}</td>
										<td
											className="max-w-[10rem] truncate px-3 py-2 font-mono text-xs text-fg-muted"
											title={m.query}
										>
											{m.query || "—"}
										</td>
										<td className="px-3 py-2">
											<span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs font-medium">
												{m.lastStatus}
											</span>
										</td>
										<UaCell ua={m.lastUserAgent} />
										<td className="px-3 py-2 font-mono text-xs tabular-nums">
											{m.hitCount}
										</td>
										<td className="px-3 py-2 text-xs text-fg-muted whitespace-nowrap">
											{formatDt(m.lastSeenAt)}
										</td>
										<td className="px-3 py-2">
											<div className="flex flex-wrap gap-2 text-xs">
												<Link
													to={`/logs/view?path=${encodeURIComponent(m.path)}`}
													className="text-accent hover:underline"
												>
													Open
												</Link>
												{segment && (
													<Link
														to={`/links/new?hash=${encodeURIComponent(segment)}`}
														className="text-fg-muted hover:text-fg"
														title={`Create short link /${segment}`}
													>
														Make link
													</Link>
												)}
											</div>
										</td>
									</tr>
								);
							})}
							{misses.length === 0 && (
								<tr>
									<td colSpan={8} className="px-3 py-6 text-center text-fg-muted">
										No misses recorded for this IP
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
}
